// `ExtensionAlertCandidatesReportEnrichmentController`'s state, ported as pure transitions so the
// form's rules are testable without a DOM.
//
// ⚠️ `canSendReport` IS STORED, NOT DERIVED, and that is upstream's behaviour rather than an
// oversight here. It is recomputed only by `onFormChanged()`, which a checkbox toggle always
// reaches but a text field reaches only when it is non-empty (`onLinkedinUrlChanged` and
// `onNotesChanged` return early on an empty model). So "type a URL, untick its box, clear the
// URL" leaves Send enabled with nothing ticked — and sends `reasons: []`. Deriving it would be
// tidier and would change what the screen does.
//
// ⚠️ THE TEXT MODELS ARE TRIMMED, and `ng-change` fires only when the trimmed model changes.
// `ng-model` on a text input or textarea trims by default (`ng-trim`), so the payload carries the
// trimmed string, and typing a trailing space is not a change — which matters because a change
// re-ticks the reason box the user may have just unticked.
import { EnrichmentReportReason, type EnrichmentReportPayload } from '@woo-ext/api'

export interface ReportEnrichmentReasonOption {
  reason: EnrichmentReportReason
  displayText: string
}

/**
 * `initReportReasonDisplay()`'s object, in its key order — which is the order `ng-repeat` over an
 * object renders AND the order `Object.keys` puts the checked reasons in the payload. It is not
 * the enum's declaration order.
 */
export const REPORT_ENRICHMENT_REASONS: readonly ReportEnrichmentReasonOption[] = [
  { reason: EnrichmentReportReason.IncorrectLinkedinProfile, displayText: 'Incorrect LinkedIn profile' },
  { reason: EnrichmentReportReason.DataEntirelyMissing, displayText: 'Enrichment data entirely missing' },
  { reason: EnrichmentReportReason.LinkedinProfileUnreachable, displayText: 'LinkedIn profile unreachable' },
  { reason: EnrichmentReportReason.LocationMismatch, displayText: 'Location mismatch' },
  { reason: EnrichmentReportReason.OutdatedWorkHistory, displayText: 'Outdated work history' },
  { reason: EnrichmentReportReason.Other, displayText: 'Other (please specify)' },
]

export interface ReportEnrichmentFormState {
  checked: Partial<Record<EnrichmentReportReason, boolean>>
  /** `ctrl.linkedinUrl` — the trimmed model; `undefined` until first typed in. */
  linkedinUrl?: string
  /** `ctrl.notes` — likewise. */
  notes?: string
  canSendReport: boolean
}

export const INITIAL_REPORT_ENRICHMENT_FORM: ReportEnrichmentFormState = { checked: {}, canSendReport: false }

/** `onFormChanged()`. */
function onFormChanged(state: ReportEnrichmentFormState): ReportEnrichmentFormState {
  const isChecked = REPORT_ENRICHMENT_REASONS.some(({ reason }) => state.checked[reason] === true)
  return { ...state, canSendReport: isChecked || !!state.linkedinUrl || !!state.notes }
}

/** A checkbox's `ng-change="ctrl.onFormChanged()"`. */
export function toggleReportReason(
  state: ReportEnrichmentFormState,
  reason: EnrichmentReportReason,
  checked: boolean,
): ReportEnrichmentFormState {
  return onFormChanged({ ...state, checked: { ...state.checked, [reason]: checked } })
}

/** The LinkedIn URL input: `ng-model` (trimmed) + `ng-change="ctrl.onLinkedinUrlChanged()"`. */
export function changeLinkedinUrl(state: ReportEnrichmentFormState, viewValue: string): ReportEnrichmentFormState {
  const model = viewValue.trim()
  if (model === state.linkedinUrl) return state
  const next = { ...state, linkedinUrl: model }
  if (!model) return next
  return onFormChanged({
    ...next,
    checked: { ...next.checked, [EnrichmentReportReason.IncorrectLinkedinProfile]: true },
  })
}

/** The notes textarea: `ng-model` (trimmed) + `ng-change="ctrl.onNotesChanged()"`. */
export function changeReportNotes(state: ReportEnrichmentFormState, viewValue: string): ReportEnrichmentFormState {
  const model = viewValue.trim()
  if (model === state.notes) return state
  const next = { ...state, notes: model }
  if (!model) return next
  return onFormChanged({ ...next, checked: { ...next.checked, [EnrichmentReportReason.Other]: true } })
}

/**
 * `sendEnrichmentReport()`'s payload. Fields that were never typed in stay absent, as
 * `undefined` does through `$http`'s `JSON.stringify`.
 */
export function buildEnrichmentReportPayload(
  state: ReportEnrichmentFormState,
  candidateId: number,
  alertId: number | null | undefined,
): EnrichmentReportPayload {
  return {
    candidateId,
    alertId,
    reasons: REPORT_ENRICHMENT_REASONS.filter(({ reason }) => state.checked[reason] === true).map(
      ({ reason }) => reason,
    ),
    linkedinUrl: state.linkedinUrl,
    notes: state.notes,
  }
}

/** The snippet's `$timeout(showReportEnrichmentSentDialogue, 1000)` after the report is sent. */
export const REPORT_ENRICHMENT_SENT_DIALOGUE_DELAY_MS = 1000

/** `showReportEnrichmentSentDialogue`'s `$timeout(() => modalInstance.close(), 3000)`. */
export const REPORT_ENRICHMENT_SENT_AUTO_CLOSE_MS = 3000
